import { getToken } from '@contexts/Token.context.jsx';
import { api, getUserData as refreshToken } from './index.js';

const maxRetries = 10;
const retryDelayFactor = 1000;

const FILE_API_URL = import.meta.env.VITE_APP_FILE_SERVICE_URL;

const ErrorType = Object.freeze({
	// Expected errors (don't report to Sentry)
	EXPECTED: 'expected',
	// Unexpected errors (report to Sentry)
	UNEXPECTED: 'unexpected',
	// Retryable errors
	RETRYABLE: 'retryable',
	// Auth errors
	AUTH: 'auth',
	// S3 auth errors.
	EXTERNAL_AUTH: 'external_auth',
	// network errors
	NETWORK: 'network_error'
});

const errorTypeMap = Object.freeze({
	204: ErrorType.EXPECTED,
	400: ErrorType.UNEXPECTED,
	401: ErrorType.AUTH,
	403: ErrorType.UNEXPECTED,
	404: ErrorType.EXPECTED,
	408: ErrorType.RETRYABLE,
	418: ErrorType.UNEXPECTED,
	429: ErrorType.RETRYABLE,
	500: ErrorType.RETRYABLE,
	502: ErrorType.RETRYABLE,
	503: ErrorType.RETRYABLE,
	504: ErrorType.RETRYABLE,
	900: ErrorType.NETWORK,
	default: ErrorType.UNEXPECTED
})

const handleApiError = async ({ error, retryFn = null, attemptNumber = 0, delayFactor = retryDelayFactor }) => {

	if (error.status || (error.response && error.response.status)) {
		const status = error.status || error.response.status;
		const errorType = errorTypeMap[status] || ErrorType.default || ErrorType.UNEXPECTED;

		// Refresh Auth and try again.
		if (errorType === ErrorType.AUTH) {
			try {
				await refreshToken();
				return retryFn ? retryFn() : null;
			} catch (refreshError) {
				console.error('Token refresh failed:', refreshError);
				return { error: refreshError, shouldRetry: false }
			}
		}
		// check if retryable and if retries has been exceeded.
		if (attemptNumber > maxRetries) {
			return { data: null, error, shouldRetry: false, message: "Max retries exceeded" }
		}

		if (errorType === ErrorType.NETWORK && retryFn) {
			await new Promise(resolve => setTimeout(() => { resolve() }, delayFactor * attemptNumber));
			return await retryFn();
		}
		// Only log unexpected errors to Sentry
		if (errorType === ErrorType.UNEXPECTED) {
			return { data: null, error, shouldRetry: false, message: "An unexpected error has occured" }
		}
		// retry if retryable
		if (errorType === ErrorType.RETRYABLE && retryFn) {

			await new Promise(resolve => setTimeout(() => { resolve() }, delayFactor * attemptNumber));

			return await retryFn();
		} else {
			return { data: null, error, shouldRetry: false, message: "Missing handler for error, not retrying on a recoverable error" };
		}
	}

	// For network errors or other unexpected errors
	// Sentry.captureException(error); // Uncomment when Sentry is integrated
	console.error('Unhandled error:', error);
	return { error, shouldRetry: false, message: "An unhandled error has occured" };
};

export const fileApiUrl = FILE_API_URL;

export class Upload {
	chunkSize = 1024 * 1024 * 50; // 50 MB in bytes
	baseURL = FILE_API_URL;
	uploadPartStates = [];

	fileRef = null;
	updateProgress = () => { };

	constructor({ fileRef, projectId, updateProgress, chunkSize, baseURL }) {
		this.fileRef = fileRef;
		this.updateProgress = updateProgress;
		this.projectId = projectId;
		this.chunkSize = chunkSize || this.chunkSize;
		this.baseURL = baseURL || this.baseURL;

		for (
			let chunkNumber = 0;
			chunkNumber * this.chunkSize <= fileRef.size;
			chunkNumber++
		) {
			this.uploadPartStates.push({
				PartNumber: chunkNumber + 1,
				startByte: chunkNumber * this.chunkSize,
				endByte: (chunkNumber + 1) * this.chunkSize,
				status: 'waiting',
			});
		}
	}
	// private functions
	/**
	 *
	 * @param {String} url
	 * @param {JSON} params
	 * @param {Number} attemptNumber
	 * @returns {Promise<Object>}
	 */

	#request = async (url, params, attemptNumber = 0) => {
		attemptNumber++;
		try {
			// make the attempt.
			const token = getToken();
			const response = await fetch(`${this.baseURL}/${url}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					authorization: `Bearer ${token}`,
				},
				mode: 'cors',
				body: JSON.stringify(params),
			});
			// If the attempt is successfull, we stop here
			if (response.ok) {
				const data = await response.json();

				return { data };
			}

			// handle api errors
			return await handleApiError({
				error: {
					status: response.status,
					response: response,
				},
				retryFn: async () => await this.#request(url, params, attemptNumber),
				attemptNumber,
			});
		} catch (error) {
			return await handleApiError({
				error: {
					status: 900,
					response: error.message,
				},
				retryFn: async () => await this.#request(url, params, attemptNumber),
				attemptNumber
			});
		}
	};

	#startMultipartUpload = async () => {
		try {
			const { data, error } = await this.#request(`startMultipartUpload`, {
				fileName: this.fileRef.name,
				projectId: this.projectId,
				size: this.fileRef.size,
			});

			if (error) {
				throw error.message
			}

			const { key, uploadId } = data;
			this.key = key;
			this.uploadId = uploadId;
		} catch (error) {
			return Promise.reject(error);
		}
	};

	#upload = async (chunkData, url, partNumber) => {
		let attemptNumber = 0;
		let resolved = false;

		while (resolved !== true && attemptNumber <= maxRetries) {
			try {
				const partResponse = await fetch(url, {
					method: 'PUT',
					body: chunkData,
					headers: {
						'Content-Type': 'multipart/form-data',
						'Content-Disposition': `attachment; name="file"; filename=${this.fileRef.name}; part=${partNumber}`,
						'Content-Length': chunkData.length,
					},
				});
				if (partResponse.ok) {
					chunkData = null;
					const ETag = partResponse.headers.get('ETag');

					resolved = true;
					return { ETag };
				}

				// Handle s3 errors.
				if (partResponse.status === 401 || partResponse.status === 403) {

					try {
						const { data, error } = await this.#request(
							`getUploadPartLink/`,
							{
								partNumber: partNumber,
								uploadId: this.uploadId,
								key: this.key,
								forceRefresh: true // Signal backend to generate a fresh URL
							}
						);
						if (error) throw error;
						const { url: newUploadUrl } = data;
						url = newUploadUrl;
						continue;
					} catch (urlError) {
						console.error('Failed to get new upload URL:', urlError);
						return Promise.reject(new Error('Failed to refresh upload URL'));
					}
				}

				if (attemptNumber >= maxRetries) {
					return Promise.reject(new Error(`Upload failed: ${partResponse.statusText}`));
				}

				attemptNumber++;
				await new Promise(resolve =>
					setTimeout(resolve, retryDelayFactor * attemptNumber)
				);

			} catch (error) {
				// Network errors
				console.warn(
					`Upload attempt ${attemptNumber}/${maxRetries} failed:`,
					error.message
				);

				if (attemptNumber >= maxRetries) {
					console.error(
						'All retry attempts failed in Upload #upload:',
						error
					);
					return Promise.reject(error);
				}


				attemptNumber++;
				await new Promise(resolve =>
					setTimeout(resolve, retryDelayFactor * attemptNumber)
				);
			}
		}
		throw new Error("Soemthing unexpected has happened during upload. Max retries exceeded");

	};

	#uploadPart = async partIndex => {
		try {
			let partState = this.uploadPartStates[partIndex];
			if (!partState)
				Promise.reject('No state found at index ', partIndex); // check if partState exists

			partState.status = 'pending';

			const { data, error } = await this.#request(
				`getUploadPartLink/`,
				{
					partNumber: partState.PartNumber,
					uploadId: this.uploadId,
					key: this.key,
				}
			);

			if (error) throw error.message;
			if (data === undefined) throw new Error('No data returned from getUploadPartLink');

			const { url: uploadUrl } = data
			let chunk = this.fileRef.slice(
				partState.startByte,
				partState.endByte
			);

			const { ETag } = await this.#upload(
				chunk,
				uploadUrl,
				partState.PartNumber
			);
			partState.status = 'completed';
			partState.ETag = ETag;

			chunk = null;

			const completedCount = this.uploadPartStates.reduce(
				(count, part) => {
					if (part.status === 'completed') count++;
					return count;
				},
				0
			);
			const progress =
				(completedCount / this.uploadPartStates.length) * 100;

			this.updateProgress(progress);

			return Promise.resolve();
		} catch (error) {
			return Promise.reject(error);
		}
	};

	#completeMultipartUpload = async () => {
		try {
			const parts = this.uploadPartStates.map(partStatus => {
				return {
					PartNumber: partStatus.PartNumber,
					ETag: partStatus.ETag,
				};
			});
			const { data, error } = await this.#request(`endMultipartUpload/`, {
				parts: parts,
				uploadId: this.uploadId,
				key: this.key,
			});
			if (error) throw error.message;
			if (data.completed === "ok") {
				return Promise.resolve();
			} else {
				return Promise.reject(error);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	};

	// Public functions
	start = async () => {
		try {
			await this.#startMultipartUpload();

			/**
			 * Refactor this block so that it allows multiple parts to run in paralell
			 */
			for (let i = 0; i < this.uploadPartStates.length; i++) {
				await this.#uploadPart(i);
			}

			await this.#completeMultipartUpload();
			this.fileRef = null;

			// Return uploadId and key for backend tracking
			return Promise.resolve({
				uploadId: this.uploadId,
				key: this.key,
			});
		} catch (error) {
			return Promise.reject(error);
		}
	};


	/**
	 * This section is for testing only. it will expose all the private functions for testing.
	 * @returns {Object} - An object containing the private functions.
	 */
	__test__ = () => {
		return {
			request: this.#request,
			startMultipartUpload: this.#startMultipartUpload,
			upload: this.#upload,
			uploadPart: this.#uploadPart,
			completeMultipartUpload: this.#completeMultipartUpload,
		};
	};
}

// TODO: Refactor this so it works with the new error handling
/**
 * Fetches data from a URI with retry logic and error handling.
 * @param {string} uri - The URI to fetch data from.
 * @param {AbortSignal} [signal] - Optional signal to abort the fetch request.
 * @param {string} [baseURL=fileApiUrl] - Base URL for the request.
 * @param {number} [attemptNumber=0] - Current retry attempt number.
 * @returns {Promise<Object>} - The fetched data as a JSON object.
 * @throws {Error} - Throws an error if all retry attempts fail.
 */
export const fetchUrl = async (
	uri,
	signal = undefined,
	baseURL = fileApiUrl,
	attemptNumber = 0
) => {
	attemptNumber++;
	try {
		// Retrieve the authentication token
		const token = getToken();

		// Perform the fetch request with the provided URI, signal, and base URL
		const response = await fetch(`${baseURL}/${uri}`, {
			method: 'GET',
			headers: {
				authorization: `Bearer ${token}`,
			},
			signal,
		});
		if (response.ok) {
			if (response.status === 204) {
				return {}; // No content to return. response is empty since there is no file content to download, and that this is expected behaviour for this endpoint.
			}
			const data = await response.json(); // is never empty since we check for 204 above. if the server is misbehaving, it will be caught below.
			return data;
		}

		return await handleApiError({
			error: {
				status: response.status,
				response: response,
			},
			retryFn: async () => await fetchUrl(uri, signal, baseURL, attemptNumber),
			attemptNumber,
		});

		// Parse the response body as JSON and return it
	} catch (err) {
		return await handleApiError({
			error: {
				status: 900,  // Network error status like Upload class uses
				response: err.message,
			},
			retryFn: async () => await fetchUrl(uri, signal, baseURL, attemptNumber),
			attemptNumber,
		});
	}
};

export const directoryExists = async directory => {
	const { data } = await api.get(`/upload/check_directory`, {
		params: { directory },
	});
	return data;
};
